import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { SupportTicket } from '@/api/support/supportApi.types';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { ListSearchInput } from '@/components/admin/shared/ListSearchInput';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { TicketPriorityBadge, TicketStatusBadge } from '@/components/support/TicketBadges';
import { TicketThread } from '@/components/support/TicketThread';
import { TicketTimeline } from '@/components/support/TicketTimeline';
import { ADMIN_LIST_PAGE_SIZE, SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { SUPPORT_TICKET_STATUSES } from '@/constants/support';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  useCloseSupportTicketMutation,
  useReopenSupportTicketMutation,
  useSupportTicketDetailQuery,
  useSupportTicketsQuery,
} from '@/hooks/useSupport';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { cn } from '@/lib/utils';
import { buildStatusTranslationKey } from '@/components/support/supportLabels';
import { NewTicketDialog } from './NewTicketDialog';

export function SupportPage() {
  const { t } = useTranslation('support');
  const navigate = useNavigate();
  const { ticketId } = useParams<{ ticketId: string }>();
  const selectedTicketId = ticketId === undefined ? null : ticketId;

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [isNewTicketOpen, setIsNewTicketOpen] = useState(false);

  useEffect(
    function resetPageOnFilterChange() {
      setPage(1);
    },
    [debouncedSearch, statusFilter]
  );

  const ticketsQuery = useSupportTicketsQuery('practitioner', page, {
    search: debouncedSearch,
    status: statusFilter.length === 0 ? null : statusFilter,
    assigneeUserId: null,
    unassigned: false,
  });
  const ticketsResult = ticketsQuery.data;

  const statusOptions = [
    { value: '', label: t('support:filters.allStatuses') },
    ...SUPPORT_TICKET_STATUSES.map(function toOption(value) {
      return { value, label: t(buildStatusTranslationKey(value)) };
    }),
  ];

  function openTicket(nextTicketId: string) {
    navigate(APPLICATION_ROUTES.workspaceSupport + '/' + nextTicketId);
  }

  return (
    <div className="p-6 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zooindigo">{t('support:title')}</h1>
          <p className="text-zoomuted text-sm mt-1">{t('support:subtitle')}</p>
        </div>
        <Button
          onClick={function openNewTicket() {
            setIsNewTicketOpen(true);
          }}
        >
          <Icon name="Plus" />
          {t('support:newTicket.action')}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className={cn('lg:col-span-1', selectedTicketId === null ? 'block' : 'hidden lg:block')}>
          <div className="space-y-3">
            <ListSearchInput
              value={searchInput}
              onChange={setSearchInput}
              placeholder={t('support:searchPlaceholder')}
            />
            <SearchableSelect
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder={t('support:filters.allStatuses')}
            />
            {ticketsQuery.isLoading === true ? (
              <div className="py-10 flex justify-center">
                <LoadingSpinner className="w-6 h-6" />
              </div>
            ) : ticketsQuery.isError === true || ticketsResult === undefined ? (
              <QueryErrorState
                onRetry={function retry() {
                  ticketsQuery.refetch();
                }}
              />
            ) : ticketsResult.items.length === 0 ? (
              <Card padding="lg" shadow={false} className="text-center text-sm text-zoomuted">
                {debouncedSearch.trim().length > 0 || statusFilter.length > 0
                  ? t('support:list.noResults')
                  : t('support:list.empty')}
              </Card>
            ) : (
              <Card padding="none" className="divide-y divide-zooborder">
                {ticketsResult.items.map(function renderRow(ticket) {
                  return (
                    <TicketRow
                      key={ticket.id}
                      ticket={ticket}
                      isSelected={ticket.id === selectedTicketId}
                      onOpen={function open() {
                        openTicket(ticket.id);
                      }}
                    />
                  );
                })}
              </Card>
            )}
            {ticketsResult === undefined ? null : (
              <ListPagination
                page={page}
                pageSize={ADMIN_LIST_PAGE_SIZE}
                totalItems={ticketsResult.totalItems}
                onPageChange={setPage}
              />
            )}
          </div>
        </div>

        <div className={cn('lg:col-span-2', selectedTicketId === null ? 'hidden lg:block' : 'block')}>
          {selectedTicketId === null ? (
            <Card padding="lg" variant="dashed" className="h-full flex items-center justify-center">
              <p className="text-sm text-zoomuted">{t('support:detail.selectPrompt')}</p>
            </Card>
          ) : (
            <TicketDetail
              ticketId={selectedTicketId}
              onBack={function goBack() {
                navigate(APPLICATION_ROUTES.workspaceSupport);
              }}
            />
          )}
        </div>
      </div>

      {isNewTicketOpen === true ? (
        <NewTicketDialog
          onClose={function closeNewTicket() {
            setIsNewTicketOpen(false);
          }}
          onCreated={function handleCreated(newTicketId) {
            setIsNewTicketOpen(false);
            openTicket(newTicketId);
          }}
        />
      ) : null}
    </div>
  );
}

interface TicketRowProps {
  ticket: SupportTicket;
  isSelected: boolean;
  onOpen: () => void;
}

function TicketRow({ ticket, isSelected, onOpen }: TicketRowProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'w-full px-4 py-3 text-left flex flex-col gap-1.5',
        isSelected === true ? 'bg-zoovpalelavender' : 'hover:bg-zoooffwhite'
      )}
    >
      <div className="flex items-center gap-2">
        <span className="flex-1 text-[13px] font-bold text-zooindigo truncate">{ticket.subject}</span>
        {ticket.unreadCount > 0 ? (
          <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-zooprimary text-zoowhite text-[10px] font-bold shrink-0">
            {ticket.unreadCount}
          </span>
        ) : null}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <TicketStatusBadge status={ticket.status} />
        <TicketPriorityBadge priority={ticket.priority} />
      </div>
    </button>
  );
}

interface TicketDetailProps {
  ticketId: string;
  onBack: () => void;
}

function TicketDetail({ ticketId, onBack }: TicketDetailProps) {
  const { t } = useTranslation('support');
  const detailQuery = useSupportTicketDetailQuery('practitioner', ticketId);
  const reopenMutation = useReopenSupportTicketMutation(ticketId);
  const closeMutation = useCloseSupportTicketMutation(ticketId);
  const detail = detailQuery.data;

  if (detailQuery.isLoading === true || detail === undefined) {
    return (
      <Card padding="lg" className="h-full flex items-center justify-center">
        <LoadingSpinner className="w-6 h-6" />
      </Card>
    );
  }

  const disabledNotice =
    detail.status === 'open'
      ? t('support:composer.waitingAssignment')
      : t('support:composer.threadClosed');
  const canReopen = detail.status === 'resolved' || detail.status === 'closed';
  const canClose = detail.status === 'resolved';

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
      <Card padding="none" className="xl:col-span-2 flex flex-col h-[70vh] overflow-hidden">
        <div className="px-4 py-3 border-b border-zooborder">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onBack}
              aria-label={t('support:detail.back')}
              className="lg:hidden text-zoomuted hover:text-zooindigo"
            >
              <Icon name="ArrowLeft" />
            </button>
            <h2 className="flex-1 text-sm font-bold text-zooindigo truncate">{detail.subject}</h2>
          </div>
          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
            <TicketStatusBadge status={detail.status} />
            <TicketPriorityBadge priority={detail.priority} />
            {detail.assigneeName === null ? null : (
              <span className="text-[11px] text-zoomuted">
                {t('support:detail.assignedTo', { name: detail.assigneeName })}
              </span>
            )}
          </div>
        </div>
        <TicketThread
          scope="practitioner"
          ticketId={ticketId}
          ownSenderKind="practitioner"
          isSendEnabled={detail.isSendEnabled}
          disabledNotice={disabledNotice}
        />
      </Card>

      <Card as="section" className="xl:col-span-1 space-y-4">
        <h3 className="text-sm font-bold text-zooindigo">{t('support:detail.timelineTitle')}</h3>
        {canReopen === true || canClose === true ? (
          <div className="flex flex-wrap gap-2">
            {canReopen === true ? (
              <Button
                variant="outline"
                size="chip"
                onClick={function reopen() {
                  reopenMutation.mutate();
                }}
              >
                <Icon name="Undo" />
                {t('support:actions.reopen')}
              </Button>
            ) : null}
            {canClose === true ? (
              <Button
                size="chip"
                onClick={function close() {
                  closeMutation.mutate();
                }}
              >
                <Icon name="Check" />
                {t('support:actions.close')}
              </Button>
            ) : null}
          </div>
        ) : null}
        <TicketTimeline events={detail.events} />
        <p className="text-[11px] text-zoomuted">
          {t('support:detail.createdAt', {
            timestamp: formatIsoDateTimeToTimestamp(detail.createdAt),
          })}
        </p>
      </Card>
    </div>
  );
}
